import { CustomerEvent, CustomerEventType } from '../types';
import { emailHtmlToText } from '../lib/emailHtml';

/**
 * Case-feed email threads (T-068). The feed view (funeral_feed) carries one
 * row per email but not the thread headers, so the headers come from
 * `communications` (see EmailHeaders) and are joined here by row id.
 */

/** The communications columns a thread needs that funeral_feed doesn't carry. */
export interface EmailHeaders {
  id: string;
  from_identifier: string | null;
  to_identifier: string | null;
  email_cc: string[] | null;
  rfc_message_id: string | null;
  email_in_reply_to: string | null;
  attachments: EmailAttachment[] | null;
  /** True once the attachments come from communication_attachments. */
  attachments_stored?: boolean;
}

export interface EmailAttachment {
  filename?: string;
  content_type?: string;
  size_bytes?: number;
  allowed?: boolean;
  /** Set when the file is on file in communication_attachments (T-139). */
  id?: string;
  stored?: boolean;
  inline?: boolean;
  /** Why the bytes weren't kept: inline_image, too_large, blocked_type, empty, upload_failed. */
  skip_reason?: string | null;
  /** The case document it was saved as, once staff filed it. */
  funeral_document_id?: string | null;
  /** That document's type, e.g. 'trustage_certificate' (T-162). */
  funeral_document_type?: string | null;
}

/** A stored-attachment row from GET /email-attachments. */
export interface StoredAttachment extends EmailAttachment {
  id: string;
  communication_id: string;
  part_index: number;
}

/**
 * Prefer the stored rows (they can be downloaded) over the communications
 * jsonb metadata, which is all older emails have. Signature logos we chose not
 * to keep are dropped from the list.
 */
export function mergeStoredAttachments(
  headers: EmailHeaders[],
  stored: StoredAttachment[],
): EmailHeaders[] {
  const byComm = new Map<string, StoredAttachment[]>();
  for (const a of stored) {
    const list = byComm.get(a.communication_id) ?? [];
    list.push(a);
    byComm.set(a.communication_id, list);
  }
  return headers.map((h) => {
    const rows = byComm.get(String(h.id));
    if (!rows?.length) return h;
    return {
      ...h,
      attachments: rows
        .sort((x, y) => x.part_index - y.part_index)
        .filter((a) => a.skip_reason !== 'inline_image'),
      attachments_stored: true,
    };
  });
}

export interface EmailThreadMessage {
  event: CustomerEvent;
  headers?: EmailHeaders;
  inbound: boolean;
  from: string | null;
  fromName: string | null;
  to: string[];
  cc: string[];
  attachments: EmailAttachment[];
}

export interface EmailThread {
  /** Stable id for the deep link: gmail thread, else the root's Message-ID, else its row id. */
  id: string;
  subject: string;
  /** Oldest first. */
  messages: EmailThreadMessage[];
  latest: EmailThreadMessage;
  gmailThreadId: string | null;
  /** Gmail mailbox that ingested the thread (metadata.mailbox), for the Gmail link. */
  mailbox: string | null;
  attachmentCount: number;
  /** The last message came in and nothing has gone out since. */
  needsReply: boolean;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Feed email rows that live in `communications` (customer_events rows have integer ids). */
export function communicationEmailIds(events: CustomerEvent[] | undefined): string[] {
  return (events || [])
    .filter((e) => e.type === CustomerEventType.EMAIL && UUID.test(String(e.id ?? '')))
    .map((e) => String(e.id));
}

const stripAngles = (id: string | null | undefined) =>
  (id || '').trim().replace(/^<|>$/g, '').toLowerCase();

/** "Re: Fwd: TR: Subject [Cleo #1042]" → "subject". Empty when nothing is left. */
export function normalizeSubject(subject: string | null | undefined): string {
  let s = (subject || '').trim();
  let prev = '';
  while (s !== prev) {
    prev = s;
    s = s.replace(/^((re|fwd?|tr)\s*:\s*)/i, '').trim();
  }
  return s
    .replace(/\s*\[cleo #\d+\]\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** One-line message text with the reply-quote removed. */
export function snippetOf(body: string | null | undefined): string {
  const { main } = splitQuotedText(cleanBody(body));
  return main.replace(/\s+/g, ' ').trim();
}

/** An email's readable text: its plain body, else its HTML as text. */
export function emailPlainText(event: Pick<CustomerEvent, 'message' | 'body_html'>): string {
  return event.message?.trim() ? event.message : emailHtmlToText(event.body_html);
}

/** The HTML to render, only for an email with no plain body. */
export function htmlOnlyBody(event: Pick<CustomerEvent, 'message' | 'body_html'>): string | null {
  return !event.message?.trim() && event.body_html?.trim() ? event.body_html : null;
}

/**
 * Feed ids whose email has no plain body. funeral_feed has no HTML column, so
 * the case feed reads these rows' body_html from communications.
 */
export function emailIdsMissingBody(events: CustomerEvent[] | undefined): string[] {
  return communicationEmailIds((events || []).filter((e) => !e.message?.trim()));
}

/**
 * What any email viewer needs to show a message: the HTML when that's all we
 * have, and the readable text split from its quoted history.
 */
export function emailBodyParts(event: Pick<CustomerEvent, 'message' | 'body_html'>) {
  const text = cleanBody(emailPlainText(event));
  return { html: htmlOnlyBody(event), text, ...splitQuotedText(text) };
}

/** Collapse the blank-line padding plaintext emails carry. */
export function cleanBody(body: string | null | undefined): string {
  return (body || '')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

// Strip the quoted history Gmail/Outlook append to replies ("On … wrote:",
// "> …", "-----Original Message-----", "From:/De:" headers).
export function splitQuotedText(body: string): { main: string; quoted: string; hasQuoted: boolean } {
  const lines = body.split('\n');
  const marker =
    /^(>|On\b.*\bwrote:|Le\b.*a\s+écrit\s*:|-{2,}\s*Original Message\s*-{2,}|_{5,}|From:\s|De\s*:\s)/i;
  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    if (marker.test(lines[i].trim())) {
      cut = i;
      break;
    }
  }
  if (cut === -1) return { main: body, quoted: '', hasQuoted: false };
  const main = lines.slice(0, cut).join('\n').trim();
  // Whole body is quoted (e.g. a forward) — keep it rather than blanking out.
  if (!main) return { main: body, quoted: '', hasQuoted: false };
  return { main, quoted: lines.slice(cut).join('\n').trim(), hasQuoted: true };
}

function toMessage(event: CustomerEvent, headers?: EmailHeaders): EmailThreadMessage {
  // Historical rows carry no direction and were always outbound.
  const inbound = event.direction === 'inbound';
  const from = headers?.from_identifier ?? (inbound ? event.contact_detail ?? null : null);
  const to = headers?.to_identifier ?? (inbound ? null : event.contact_detail ?? null);
  const fromName = inbound
    ? event.metadata?.from_name || event.recipient_name || null
    : event.user_name || null;
  return {
    event,
    headers,
    inbound,
    from,
    fromName,
    to: to ? [to] : [],
    cc: headers?.email_cc?.filter(Boolean) ?? [],
    attachments: headers && Array.isArray(headers.attachments) ? headers.attachments : [],
  };
}

/**
 * Group the feed's email rows into threads. Two rows share a thread when any
 * of these link them, in the order the case-email matcher uses them:
 *   1. same gmail_thread_id
 *   2. In-Reply-To names the other's Message-ID
 *   3. metadata.thread_parent_id names the other row
 *   4. same subject once "Re:", "Fwd:" and the [Cleo #n] tag are stripped —
 *      what ties our own replies (sent without In-Reply-To) back to the thread.
 * A row nothing links to is a thread of one.
 */
export function groupEmailThreads(
  events: CustomerEvent[] | undefined,
  headersById: Map<string, EmailHeaders> = new Map(),
): EmailThread[] {
  const emails = (events || []).filter((e) => e.type === CustomerEventType.EMAIL);
  if (emails.length === 0) return [];

  const parent = emails.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
  };

  const byKey = new Map<string, number>();
  const link = (key: string, i: number) => {
    const seen = byKey.get(key);
    if (seen === undefined) byKey.set(key, i);
    else union(seen, i);
  };
  const byRfc = new Map<string, number>();
  const byId = new Map<string, number>();
  emails.forEach((e, i) => {
    byId.set(String(e.id), i);
    const rfc = stripAngles(headersById.get(String(e.id))?.rfc_message_id);
    if (rfc) byRfc.set(rfc, i);
  });

  emails.forEach((e, i) => {
    const h = headersById.get(String(e.id));
    if (e.gmail_thread_id) link(`g:${e.gmail_thread_id}`, i);
    const parentByRfc = byRfc.get(stripAngles(h?.email_in_reply_to));
    if (parentByRfc !== undefined) union(parentByRfc, i);
    const parentById = byId.get(String(e.metadata?.thread_parent_id ?? ''));
    if (parentById !== undefined) union(parentById, i);
    const subj = normalizeSubject(e.subject);
    if (subj) link(`s:${subj}`, i);
  });

  const groups = new Map<number, number[]>();
  emails.forEach((_, i) => {
    const r = find(i);
    groups.set(r, [...(groups.get(r) || []), i]);
  });

  const threads: EmailThread[] = [];
  groups.forEach((idxs) => {
    const messages = idxs
      .map((i) => toMessage(emails[i], headersById.get(String(emails[i].id))))
      .sort((a, b) => a.event.timestamp.localeCompare(b.event.timestamp));
    const root = messages[0];
    const latest = messages[messages.length - 1];
    const gmailThreadId = messages.find((m) => m.event.gmail_thread_id)?.event.gmail_thread_id ?? null;
    const mailbox = messages.find((m) => m.event.metadata?.mailbox)?.event.metadata?.mailbox ?? null;
    const rootRfc = stripAngles(root.headers?.rfc_message_id);
    threads.push({
      id: gmailThreadId || rootRfc || String(root.event.id),
      subject: root.event.subject || latest.event.subject || '(no subject)',
      messages,
      latest,
      gmailThreadId,
      mailbox,
      attachmentCount: messages.reduce((n, m) => n + attachmentCountOf(m), 0),
      needsReply: latest.inbound,
    });
  });
  return threads.sort((a, b) => a.latest.event.timestamp.localeCompare(b.latest.event.timestamp));
}

/** Gmail web link for a thread in the ingesting mailbox. */
export function gmailThreadUrl(thread: Pick<EmailThread, 'gmailThreadId' | 'mailbox'>): string | null {
  if (!thread.gmailThreadId) return null;
  const account = thread.mailbox ? `?authuser=${encodeURIComponent(thread.mailbox)}` : '0/';
  return `https://mail.google.com/mail/u/${account}#all/${thread.gmailThreadId}`;
}

/**
 * How many attachments to show for a message. Stored rows are exact (minus the
 * signature logos we skip); older rows only have metadata, which may count
 * parts the jsonb list lacks.
 */
export function attachmentCountOf(m: EmailThreadMessage): number {
  if (m.headers?.attachments_stored) return m.attachments.length;
  return Math.max(m.attachments.length, Number(m.event.metadata?.attachment_count) || 0);
}

export function formatBytes(n: number | undefined): string {
  if (!n || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** "application/pdf" / "scan.PDF" → "PDF". */
export function fileTypeLabel(a: EmailAttachment): string {
  const ext = (a.filename || '').split('.').pop();
  if (ext && ext !== a.filename && ext.length <= 5) return ext.toUpperCase();
  const sub = (a.content_type || '').split('/').pop() || '';
  return sub.replace(/^vnd\..*/, 'file').slice(0, 5).toUpperCase() || 'FILE';
}
